import { useEffect, useRef, useState, type FormEvent, type MouseEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  api,
  ApiError,
  isTerminal,
  statusKind,
  statusLabel,
  type MeetingListItem,
} from '../lib/api.js';
import { formatDate, formatDuration, platformLabel } from '../lib/format.js';
import { currentBrand } from '../lib/brand.js';
import { DownloadVideoButton } from '../components/DownloadVideoButton.js';

export function LibraryPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const qParam = params.get('q') ?? '';
  const [query, setQuery] = useState(qParam);
  const [meetings, setMeetings] = useState<MeetingListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const agentDoor = currentBrand().id === 'agentmeetings';

  const queryRef = useRef(query);
  queryRef.current = query;
  useEffect(() => {
    if (qParam === queryRef.current.trim()) return;
    setQuery(qParam);
  }, [qParam]);

  useEffect(() => {
    const next = query.trim();
    if (next === qParam) return;
    const timer = setTimeout(() => {
      if (next) setParams({ q: next }, { replace: true });
      else setParams({}, { replace: true });
    }, 280);
    return () => clearTimeout(timer);
  }, [query, qParam, setParams]);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        const target = e.target as HTMLElement | null;
        const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
        if (typing && target !== searchRef.current) return;
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const q = qParam;

    async function load(): Promise<void> {
      let keepPolling = true;
      try {
        const rows = await api.listMeetings(q || undefined);
        if (!alive) return;
        setMeetings(rows);
        setError(null);
        setSelected((prev) => {
          const ids = new Set(rows.map((r) => r.id));
          const next = new Set<string>();
          for (const id of prev) if (ids.has(id)) next.add(id);
          return next;
        });
        keepPolling = !q && rows.some((m) => !isTerminal(m.status));
      } catch (err) {
        if (!alive) return;
        setError(err instanceof Error ? err.message : String(err));
        keepPolling = !(err instanceof ApiError && err.status === 401);
      }
      if (alive && keepPolling) timer = setTimeout(() => void load(), 5000);
    }

    void load();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [qParam, reloadKey]);

  function onSearch(e: FormEvent): void {
    e.preventDefault();
    const next = query.trim();
    if (next) setParams({ q: next }, { replace: true });
    else setParams({}, { replace: true });
  }

  function toggleOne(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll(): void {
    if (!meetings) return;
    if (selected.size === meetings.length) setSelected(new Set());
    else setSelected(new Set(meetings.map((m) => m.id)));
  }

  async function deleteIds(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    setBusy(true);
    try {
      if (ids.length === 1) await api.deleteMeeting(ids[0]!);
      else await api.bulkDeleteMeetings(ids);
      setSelected(new Set());
      setConfirmBulk(false);
      setReloadKey((n) => n + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onRowDelete(e: MouseEvent, id: string): Promise<void> {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Delete this meeting? This cannot be undone.')) return;
    await deleteIds([id]);
  }

  const allSelected = !!(meetings && meetings.length > 0 && selected.size === meetings.length);
  const total = meetings?.length ?? 0;

  const search = (
    <form className={`library-search${agentDoor ? ' library-search--wide' : ''}${qParam ? ' library-search--clear' : ''}`} onSubmit={onSearch} role="search">
      <input
        ref={searchRef}
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search meetings by title, speaker..."
        aria-label="Search meetings"
      />
      {agentDoor && <kbd className="library-search__kbd">⌘K</kbd>}
      {qParam && (
        <button
          type="button"
          className="library-search__clear"
          onClick={() => { setQuery(''); setParams({}, { replace: true }); }}
        >
          Clear
        </button>
      )}
    </form>
  );

  return (
    <div className="library-page">
      <div className="library-toolbar">
        <div>
          <h1 className="page-title">
            Meeting Library
            {meetings && (
              <span className="count-chip count-chip--brand">
                {total} {qParam ? (total === 1 ? 'match' : 'matches') : 'total'}
              </span>
            )}
          </h1>
          <p className="page-subtitle">All recorded meetings across your team</p>
        </div>
        <div className="library-toolbar__actions">
          {!agentDoor && search}
          <Link to="/join" className="btn library-new">
            <PlusIcon />
            New Meeting
          </Link>
        </div>
      </div>

      {error && <div className="form-error" style={{ marginBottom: 16 }}>Failed to load meetings: {error}</div>}

      <div className="library-panel">
        {(agentDoor || (meetings != null && selected.size > 0)) && (
          <div className="library-panel__bar">
            {agentDoor && search}
            {meetings != null && meetings.length > 0 && selected.size > 0 && (
              <div className="library-bulk">
                <span className="library-bulk__count">{selected.size} selected</span>
                <button type="button" className="btn btn--ghost btn--sm" onClick={toggleAll}>
                  {allSelected ? 'Clear selection' : 'Select all'}
                </button>
                <button
                  type="button"
                  className="btn btn--danger-ghost btn--sm"
                  disabled={busy}
                  onClick={() => setConfirmBulk(true)}
                >
                  Delete
                </button>
                <button type="button" className="btn btn--tertiary btn--sm" onClick={() => setSelected(new Set())}>
                  Clear
                </button>
              </div>
            )}
          </div>
        )}

        {meetings === null && !error && (
          <div className="library-empty">
            <span className="spinner" /> Loading meetings&hellip;
          </div>
        )}

        {meetings && meetings.length === 0 && (
          <div className="library-empty">
            <h2>{qParam ? 'No matches' : 'No meetings yet'}</h2>
            <p>
              {qParam
                ? `Nothing found for “${qParam}”.`
                : `Paste a Zoom, Meet, or Teams link to have ${currentBrand().name} join and record.`}
            </p>
            {!qParam && (
              <div style={{ marginTop: 16 }}>
                <Link to="/join" className="btn library-new">Start your first meeting</Link>
              </div>
            )}
          </div>
        )}

        {meetings && meetings.length > 0 && (
          <>
            <div className="library-cards">
              {meetings.map((m) => (
                <article className={`library-card${selected.has(m.id) ? ' library-card--selected' : ''}`} key={m.id}>
                  <label className="library-card__check">
                    <input
                      type="checkbox"
                      checked={selected.has(m.id)}
                      onChange={() => toggleOne(m.id)}
                      aria-label={`Select ${m.title ?? 'meeting'}`}
                    />
                  </label>
                  <span className="library-card__icon" aria-hidden="true"><MeetingGlyph /></span>
                  <Link to={`/meetings/${m.id}`} className="library-card__main">
                    <h2 className="library-card__title">{meetingTitle(m)}</h2>
                    <MeetingMeta meeting={m} />
                  </Link>
                  <div className="library-card__actions">
                    <StatusBadge
                      kind={statusKind(m.status)}
                      label={m.status === 'complete' && m.hasSummary ? 'Notes ready' : statusLabel(m.status)}
                    />
                    {m.videoStored && (
                      <DownloadVideoButton meetingId={m.id} available compact />
                    )}
                    <Link to={`/meetings/${m.id}`} className="library-card__open">Open</Link>
                    <button
                      type="button"
                      className="library-card__delete"
                      title="Delete meeting"
                      aria-label="Delete meeting"
                      onClick={(e) => void onRowDelete(e, m.id)}
                      disabled={busy}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </article>
              ))}
            </div>
            <div className="library-foot">
              Showing {total} meeting{total === 1 ? '' : 's'}
            </div>
          </>
        )}
      </div>

      {confirmBulk && (
        <div className="dialog-backdrop" role="dialog" aria-modal="true">
          <div className="dialog">
            <h2>Delete {selected.size} meetings?</h2>
            <p>This cannot be undone.</p>
            <div className="dialog__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setConfirmBulk(false)} disabled={busy}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--danger"
                disabled={busy}
                onClick={() => void deleteIds([...selected])}
              >
                {busy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function meetingTitle(meeting: MeetingListItem): string {
  return meeting.title ?? `${platformLabel(meeting.platform)} meeting`;
}

function shown(value: string | null | undefined): string | null {
  if (!value || value === '—' || value === '-') return null;
  return value;
}

function MeetingMeta({ meeting }: { meeting: MeetingListItem }): JSX.Element {
  const when = meeting.startedAt ?? meeting.createdAt;
  const parts: ReactNode[] = [
    <span className="library-card__platform" key="platform">
      <span className="library-card__pdot" aria-hidden="true" />
      {platformLabel(meeting.platform)}
    </span>,
  ];
  const whenLabel = shown(formatDate(when));
  const duration = meeting.durationSeconds && meeting.durationSeconds > 0
    ? shown(formatDuration(meeting.durationSeconds))
    : null;
  if (whenLabel) parts.push(<span key="when">{whenLabel}</span>);
  if (duration) parts.push(<span key="duration" className="library-card__platform">{duration}</span>);
  if (meeting.keywordHits && meeting.keywordHits.length > 0) {
    parts.push(<span className="kw-hit" key="kw">{meeting.keywordHits.map((h) => h.phrase).join(', ')}</span>);
  }
  if (meeting.searchSnippet) {
    parts.push(<span className="library-card__snippet" key="snip">{meeting.searchSnippet}</span>);
  }

  return (
    <div className="library-card__meta">
      {parts.map((part, i) => (
        <span key={i} style={{ display: 'contents' }}>
          {i > 0 && <span className="library-card__sep" aria-hidden="true">·</span>}
          {part}
        </span>
      ))}
    </div>
  );
}

function PlusIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function MeetingGlyph(): JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="6" width="12" height="12" rx="2" />
      <path d="M15 10.5 21 7.5v9l-6-3" />
    </svg>
  );
}

function TrashIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </svg>
  );
}

export function StatusBadge({
  kind,
  label,
}: {
  kind: 'active' | 'complete' | 'failed' | 'idle';
  label: string;
}): JSX.Element {
  return (
    <span className={`status-badge status-badge--${kind}`}>
      <span className="status-badge__dot" />
      {label}
    </span>
  );
}
